import React, { useEffect, useState, useCallback } from 'react';
import { Compose } from '../components/Compose.jsx';
import { MessageView } from '../components/MessageView.jsx';

// Three-pane mailbox: folders | message list | reading pane.
export function Mailbox({ session, onLogout }) {
  const { jmap, email } = session;
  const [mailboxes, setMailboxes] = useState([]);
  const [activeMailbox, setActiveMailbox] = useState(null);
  const [emails, setEmails] = useState([]);
  const [selected, setSelected] = useState(null);
  const [composing, setComposing] = useState(false);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState(null);

  // Load mailboxes on mount.
  useEffect(() => {
    (async () => {
      try {
        const boxes = await jmap.listMailboxes();
        setMailboxes(boxes);
        const inbox = boxes.find((b) => b.role === 'inbox') || boxes[0];
        setActiveMailbox(inbox?.id);
      } catch (e) { setErr(e.message); }
    })();
  }, [jmap]);

  // Load emails when the active mailbox changes.
  const refresh = useCallback(async (mbId) => {
    if (!mbId) return;
    setLoading(true); setErr(null);
    try {
      setEmails(await jmap.listEmails(mbId));
    } catch (e) { setErr(e.message); }
    setLoading(false);
  }, [jmap]);

  useEffect(() => { refresh(activeMailbox); }, [activeMailbox, refresh]);

  return (
    <div className="km-app">
      <header className="km-topbar">
        <div className="km-topbar-brand">
          <img src="/brand/mark.svg" width="28" height="28" alt="" />
          <span className="km-wordmark-sm">krypta<span>mail</span></span>
        </div>
        <div className="km-topbar-right">
          <span className="km-pqc-chip">🔐 PQC active</span>
          <span className="km-dim km-mono" style={{ fontSize: 13 }}>{email}</span>
          <button className="km-btn-ghost" onClick={onLogout}>Sign out</button>
        </div>
      </header>

      <div className="km-body">
        <nav className="km-folders">
          <button className="km-btn-primary km-compose-btn" onClick={() => setComposing(true)}>
            ✎ Compose
          </button>
          {mailboxes.map((mb) => (
            <button
              key={mb.id}
              className={`km-folder ${mb.id === activeMailbox ? 'active' : ''}`}
              onClick={() => { setSelected(null); setActiveMailbox(mb.id); }}
            >
              <span>{folderIcon(mb.role)} {mb.name}</span>
              {mb.unreadEmails > 0 && <span className="km-badge">{mb.unreadEmails}</span>}
            </button>
          ))}
        </nav>

        <section className="km-list">
          {loading && <div className="km-list-empty km-dim">Loading…</div>}
          {err && <div className="km-list-empty km-login-error">⚠️ {err}</div>}
          {!loading && !emails.length && <div className="km-list-empty km-faint">No messages</div>}
          {emails.map((m) => (
            <button
              key={m.id}
              className={`km-list-item ${selected?.id === m.id ? 'active' : ''}`}
              onClick={() => setSelected(m)}
            >
              <div className="km-list-from">{displayFrom(m.from)}</div>
              <div className="km-list-subject">
                {isPqc(m) && <span className="km-lock">🔐</span>}
                {m.subject || '(no subject)'}
              </div>
              <div className="km-list-preview km-faint">{m.preview}</div>
            </button>
          ))}
        </section>

        <section className="km-reader">
          {selected
            ? <MessageView email={selected} session={session} />
            : <div className="km-reader-empty km-faint">Select a message</div>}
        </section>
      </div>

      {composing && (
        <Compose
          session={session}
          onClose={() => setComposing(false)}
          onSent={() => { setComposing(false); refresh(activeMailbox); }}
        />
      )}
    </div>
  );
}

function folderIcon(role) {
  return { inbox: '📥', sent: '📤', drafts: '📝', trash: '🗑', junk: '⚠️' }[role] || '📁';
}
function displayFrom(from) {
  if (!from?.length) return '(unknown)';
  return from[0].name || from[0].email || '(unknown)';
}
function isPqc(m) {
  return (m.keywords && m.keywords['$kryptamail-pqc']) ||
    (m.subject || '').includes('[KryptaMail encrypted');
}
